import styled, { css, DefaultTheme, keyframes } from 'styled-components';

import { Row } from "global/components/micros/Layout/Row/styles";

import { DialogProps } from './types';

const borderRadius = '0.75rem';

const modifiers = {
	light: (theme: DefaultTheme) => css`
		${Dialog} {
			background: ${theme.colors.white};
		}

		${Iconref} {
			i {
				color: ${theme.colors.black100};
			}
		}

		${IconClose} {
			i {
				color: ${theme.colors.black100};
			}
		}

		${ListItem} {
			i {
				color: ${theme.colors.portoSeguros100};
			}
		}

		${PreTitle} {
			color: ${theme.colors.black65};
		}

		${Title} {
			color: ${theme.colors.black85};
		}

		${SubTitle} {
			color: ${theme.colors.black85};
		}

		${SubTitle2} {
			color: ${theme.colors.black85};
		}

		${Text} {
			color: ${theme.colors.black75};
		}
	`,
	dark: (theme: DefaultTheme) => css`
		${Dialog} {
			background: ${theme.colors.black85};
		}

		${Iconref} {
			i {
				color: ${theme.colors.white};
			}
		}

		${IconClose} {
			i {
				color: ${theme.colors.black45};
			}
		}

		${ListItem} {
			i {
				color: ${theme.colors.black45};
			}
		}

		${HeaderContainer} {
			background: ${theme.colors.black85};
		}

		${ContextContainer} {
			background: ${theme.colors.black85};
		}

		${FooterContainer} {
			background: ${theme.colors.black85};
		}

		${PreTitle} {
			color: ${theme.colors.black60};
		}

		${Title} {
			color: ${theme.colors.white};
		}

		${SubTitle} {
			color: ${theme.colors.white};
		}

		${SubTitle2} {
			color: ${theme.colors.black60};
		}

		${Text} {
			color: ${theme.colors.black15};
		}

		${ButtonGhost} {
			button {
				color: ${theme.colors.white};
			}
		}

		${ButtonPrimary} {
			button {
				background-color: ${theme.colors.white};
				color: ${theme.colors.portoSeguros100};
			}
		}

		${ButtonSecondary} {
			button {
				background-color: ${theme.colors.white};
				color: ${theme.colors.portoSeguros100};
			}
		}
	`,

	small: () => css`
		${Dialog} {
			max-width: 23rem;
	    @media all and (max-width: 412px){
            max-width: 272px;
          }
		}
	`,

	medium: () => css`
		${Dialog} {
			max-width: 37.5rem;
		}
	`,

	large: () => css`
		${Dialog} {
			max-width: 50.5rem;
		}
	`,

	columnReverse: () => css`
		${ButtonsContainer} {
			flex-direction: column-reverse;
			align-items: stretch;

			button {
				width: 100%;
			}
		}
		${ButtonSecondary} {
			margin-left: initial;
		}
	`,
	row: () => css`
		${ButtonsContainer} {
			flex-direction: row;
		}
		${ButtonSecondary} {
			margin-left: auto;
		}
	`,
};

const fadeIn = keyframes`
	0% {
    	background: rgba(0, 0, 0, 0);
  	}
    100% {
      background: rgba(0, 0, 0, 0.4);
  	}
`;

export const Overlay = styled.div`
	${({ theme }) => css`
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		z-index: ${theme.layers.overlayModal};
		animation: ${fadeIn} 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
		display: flex;
		align-items: center;
		justify-content: center;
	`}
`;

export const Container = styled(Row)<
	Pick<DialogProps, 'mode' | 'variant' | 'buttonContainerLayout'>
>`
	ul {
		list-style-position: inside;
		padding-left: 0.5rem;
	}
	${({ theme, mode, variant, buttonContainerLayout }) => css`
		${!!mode && modifiers[mode](theme)};
		${!!variant && modifiers[variant]()};
		${!!buttonContainerLayout && modifiers[buttonContainerLayout!]()};
	`}
`;

export const Text = styled.p`
	${({ theme }) => css`
		font-family: ${theme.font.family.OpenSans};
		font-weight: ${theme.font.weight.regular};
		font-size: ${theme.font.sizes.small};
		line-height: ${theme.font.sizes.large};
		color: ${theme.colors.black75};
		font-style: normal;
	@media all and (max-width: 412px){
        font-size: 0.8125rem;
       }	
	`}
`;

export const Dialog = styled.div`
	${({ theme }) => css`
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		padding: 1.25rem ${theme.spacings.medium};
		gap: ${theme.spacings.large};
		background: ${theme.colors.white};
		border-radius: ${borderRadius};
		margin: auto;
		max-height: 80vh;
		overflow-y: auto;
	`}
`;

export const HeaderContainer = styled.div`
	${({ theme }) => css`
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: ${theme.spacings.small};
		width: 100%;
		mix-blend-mode: normal;
		border-radius: ${borderRadius} ${borderRadius} 0 0;
	`}
`;

export const ContextContainer = styled.div`
	${({ theme }) => css`
		width: 100%;
		max-height: 17.5rem;
		overflow-y: auto;
		padding: ${theme.spacings.xsmall} 0;
		padding-right: ${theme.spacings.xsmall};

		::-webkit-scrollbar {
			width: 2.5rem;
		}

		::-webkit-scrollbar-thumb {
			width: 0.25rem;
			height: 3.1875rem;
			background: ${theme.colors.black30};
			border-radius: 0.4375rem;
		}
	`}
`;

export const FooterContainer = styled.div`
	${() => css`
		width: 100%;
		min-height: 2rem;
		mix-blend-mode: normal;
		border-radius: 0 0 ${borderRadius} ${borderRadius};
	`}
`;

export const Icons = styled.div`
	${() => css`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-start;
		width: 100%;
	`}
`;

export const IconClose = styled.button`
	margin-left: auto;
	background: none;
	border: none;
	cursor: pointer;
`;

export const PreTitle = styled.p`
	${({ theme }) => css`
		font-family: ${theme.font.family.OpenSans};
		font-weight: ${theme.font.weight.bold};
		font-size: ${theme.font.sizes.small};
		font-style: normal;
		line-height: 0.125rem;
		letter-spacing: 0.02em;
		text-transform: uppercase;
		color: ${theme.colors.black65};
	`}
`;

export const Title = styled.h2`
	${({ theme }) => css`
		font-family: ${theme.font.family.portoRoobert};
		font-weight: ${theme.font.weight.semibold};
		font-size: ${theme.font.sizes.xlarge};
		font-style: normal;
		line-height: ${theme.font.sizes.xlarge};
		letter-spacing: 0.02em;
		color: ${theme.colors.black85};
	`}
`;

export const SubTitle = styled.p`
	${({ theme }) => css`
		font-family: ${theme.font.family.OpenSans};
		font-weight: ${theme.font.weight.bold};
		font-size: ${theme.font.sizes.medium};
		font-style: normal;
		line-height: ${theme.font.sizes.xlarge};
		color: ${theme.colors.black85};
	`}
`;

export const SubTitle2 = styled(Text)`
	${({ theme }) => css`
		color: ${theme.colors.black85};
	`}
`;

export const ListContainer = styled.ul`
	list-style: none;
`;

export const ListItem = styled.li`
	${({ theme }) => css`
		display: flex;
		gap: ${theme.spacings.small};
		margin-bottom: ${theme.spacings.small};

		&:last-child {
			margin-bottom: 0;
		}
	`}
`;

export const ScrollText = styled(Text)`
	word-wrap: break-word;
`;

export const Note = styled(Text)`
	${({ theme }) => css`
		font-size: ${theme.font.sizes.xsmall};
	`}
`;

export const ButtonsContainer = styled.div`
	${({ theme }) => css`
		width: 100%;
		display: flex;
		align-items: center;
		gap: ${theme.spacings.medium};
    justify-content: end;
	`}
`;

export const StoreButtonsContainer = styled.div`
	${({ theme }) => css`
		display: flex;
		justify-content: center;
		gap: ${theme.spacings.small};
	`}
`;

export const StoreButton = styled.a``;

export const StoreImg = styled.img`
	width: 7.75rem;
`;

export const ButtonGhost = styled.div``;

export const ButtonPrimary = styled.div``;

export const ButtonSecondary = styled.div``;

export const Iconref = styled.div``;
